import React from "react";
import PropTypes from "prop-types";
import { Field, ErrorMessage, useFormikContext } from "formik";
import classNames from "classnames";
import { useIntl } from "react-intl";

import { states } from "../states";
import { getValueFromStringObjPath } from "../webform-elements";
import DashedField from "./dashed-field";

const SelectField = ({ item, compositeIndex, compositeParent }) => {
  const intl = useIntl();
  const { values, handleChange, handleBlur, errors, touched } =
    useFormikContext();

  const { invisible, visible, enabled, disabled, optional, required } = states(
    item["#states"],
    values
  );

  const value = compositeParent
    ? typeof compositeIndex !== "undefined"
      ? getValueFromStringObjPath(values, compositeParent)[compositeIndex][
          item.id
        ]
      : getValueFromStringObjPath(values, compositeParent)[item.id]
    : values[item.id];
  const identifier = compositeParent
    ? typeof compositeIndex !== "undefined"
      ? `${compositeParent}[${compositeIndex}][${item.id}]`
      : `${compositeParent}[${item.id}]`
    : item.id;

  return (
    <div
      className={classNames({
        "form-group": true,
        hidden: invisible || !visible,
      })}
      style={item["#flex"] ? { flex: item["#flex"] } : {}}
    >
      <label htmlFor={identifier} className="form-label">
        {item["#title"]}
        {(!!item["#required"] || required) && !optional && visible && (
          <span className="required">*</span>
        )}
      </label>

      <DashedField>
        <Field
          id={identifier}
          name={identifier}
          as="select"
          className={classNames({
            "form-control": true,
            "is-invalid": errors[item.id] && touched[item.id],
          })}
          onChange={handleChange}
          onBlur={handleBlur}
          value={value}
          required={(!!item["#required"] || required) && !optional && visible}
          disabled={!enabled || disabled || item["#disabled"]}
          multiple={!!item["#multiple"]}
        >
          {/* @todo Translate */}
          <option
            //disabled={(!!item["#required"] || required) && !optional && visible}
            value={item["#empty_value"] ? item["#empty_value"] : ""}
          >
            {item["#empty_option"]
              ? item["#empty_option"]
              : intl.formatMessage({ id: "webform.select_empty" })}
          </option>
          {Object.keys(item["#options"]).map((key, i) => (
            <option key={i} value={key}>
              {item["#options"][key]}
            </option>
          ))}
        </Field>
      </DashedField>

      <ErrorMessage
        role="region"
        aria-live="polite"
        component="span"
        name={identifier}
        className="error-message"
      />

      {!!item["#description"] && (
        <small
          className="form-description text-muted form-text"
          dangerouslySetInnerHTML={{ __html: item["#description"] }}
        />
      )}
    </div>
  );
};

SelectField.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    "#states": PropTypes.object,
    "#flex": PropTypes.number,
    "#title": PropTypes.string,
    "#required": PropTypes.bool,
    "#description": PropTypes.string,
    "#multiple": PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    "#multiple_error": PropTypes.string,
    "#options": PropTypes.objectOf(PropTypes.string),
    "#empty_option": PropTypes.string,
    "#empty_value": PropTypes.string,
    "#disabled": PropTypes.bool,
  }),
  compositeIndex: PropTypes.number,
  compositeParent: PropTypes.string,
};

export default SelectField;
